import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { transactionAPI } from '../services/api';
import { formatDate, formatContractAreaSizeWithUnit } from '../utils/formatters';
import {
    fillPdfForm2Multiple,
    createPdfPreviewTab,
    showPdfInPreviewTab,
    showPdfPreviewError,
} from '../utils/pdfFormFiller';

const GraveRecord = () => {
    const { id: contractId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isGenerating, setIsGenerating] = useState(false);

    // 계약 정보 조회
    const { data: contract, isLoading } = useQuery({
        queryKey: ['contract', contractId],
        queryFn: () => transactionAPI.getById(contractId),
        select: (response) => response.data,
        enabled: !!contractId,
    });

    // 묘적부 문서 생성 및 다운로드 (PDF 폼 사용)
    const handleDownload = async () => {
        if (!contract) {
            alert('계약 정보를 찾을 수 없습니다.');
            return;
        }

        const previewWindow = createPdfPreviewTab();
        if (!previewWindow) {
            alert('팝업이 차단되어 PDF를 열 수 없습니다. 브라우저에서 이 사이트의 팝업을 허용해 주세요.');
            return;
        }

        setIsGenerating(true);
        try {
            const pdfResults = await fillPdfForm2Multiple(contract);
            const baseName = `묘적부_${contract.mausoleum?.member_number || contract.id}`;

            pdfResults.forEach((result, idx) => {
                const fileName = pdfResults.length === 1
                    ? `${baseName}.pdf`
                    : `${baseName}_${result.index}.pdf`;
                const targetWindow = idx === 0
                    ? previewWindow
                    : createPdfPreviewTab();

                if (!targetWindow) {
                    return;
                }

                showPdfInPreviewTab(targetWindow, result.pdfBytes, fileName);
            });
        } catch (error) {
            console.error('PDF 생성 중 오류:', error);
            if (!previewWindow.closed) {
                showPdfPreviewError(previewWindow, error.message);
            } else {
                alert(`PDF 생성 중 오류가 발생했습니다: ${error.message}`);
            }
        } finally {
            setIsGenerating(false);
        }
    };

    if (isLoading) {
        return <div className="min-h-screen bg-gray-50 p-6">로딩 중...</div>;
    }

    if (!contract) {
        return <div className="min-h-screen bg-gray-50 p-6">계약 정보를 찾을 수 없습니다.</div>;
    }

    // 연고자 정보 수집 (중복 제거 및 ContractDetail과 로직 동기화)
    const acquaintancesSet = new Set();
    const allAcquaintances = [];
    
    // 1. Transaction에 직접 연결된 연고자 (예정지 등)
    if (contract.acquaintances && Array.isArray(contract.acquaintances)) {
        contract.acquaintances.forEach(acc => {
            const key = `${acc.id}-${acc.name}`;
            if (!acquaintancesSet.has(key)) {
                acquaintancesSet.add(key);
                allAcquaintances.push(acc);
            }
        });
    }

    // 2. Dead를 통한 연고자 수집
    contract.deads?.forEach(dead => {
        [dead.acquaintance1, dead.acquaintance2].forEach(acc => {
            if (acc) {
                const key = `${acc.id}-${acc.name}`;
                if (!acquaintancesSet.has(key)) {
                    acquaintancesSet.add(key);
                    allAcquaintances.push(acc);
                }
            }
        });
    });

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* 헤더 */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(`/contracts/${contract.id}`)}
                            className="btn btn-outline flex items-center gap-2"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">묘적부</h1>
                            {/* <p className="text-gray-600">계약번호: {contract.id}</p> */}
                        </div>
                    </div>
                    
                    <button
                        onClick={handleDownload}
                        disabled={isGenerating}
                        className="btn btn-primary flex items-center gap-2"
                    >
                        <Download size={16} />
                        {isGenerating ? '생성 중...' : '문서 다운로드'}
                    </button>
                </div>

                {/* 묘적부 내용 (UI 유지, 데이터 바인딩만 수정) */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="border-b p-6">
                        <h2 className="text-2xl font-bold text-center text-gray-900">묘적부</h2>
                    </div>

                    {/* 회원정보 섹션 */}
                    <div className="p-6 border-b">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">회원정보</h3>
                        <div className="grid grid-cols-1 gap-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">묘적번호</label>
                                    <p className="text-gray-900 font-medium">{contract.mausoleum?.member_number || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">등록일자</label>
                                    <p className="text-gray-900">{formatDate(contract.contract_date)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">계약평수</label>
                                    <p className="text-gray-900">{formatContractAreaSizeWithUnit(contract.mausoleum?.area_size ?? contract.mausoleum?.size)}</p>
                                </div>
                            </div>

                            {/* 연고자 정보들 */}
                            {allAcquaintances.map((acquaintance, index) => {
                                const fullAddress = [acquaintance.road_address, acquaintance.address_detail].filter(Boolean).join(', ') || acquaintance.address || '-';
                                return (
                                    <div key={index} className="border-t pt-4">
                                        <div className="grid grid-cols-3 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 mb-1">
                                                    신청자{index + 1} ({acquaintance.relationship || '기타'})
                                                </label>
                                                <p className="text-gray-900 font-medium">{acquaintance.name}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 mb-1">주민번호</label>
                                                <p className="text-gray-900">{acquaintance.resident_number || acquaintance.resident_id || '-'}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 mb-1">연락처</label>
                                                <p className="text-gray-900">{acquaintance.phone || acquaintance.mobile || '-'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 mt-2">
                                            <div className="col-span-2">
                                                <label className="text-sm font-medium text-gray-500 mb-1">신청주소</label>
                                                <p className="text-gray-900 text-sm">{fullAddress}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* 설묘자 정보 섹션 */}
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">설묘자 정보</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">묘역위치</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">설묘평수</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">설묘구분</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">성명(성별)</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">주민번호</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">장례구분2</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">설묘일자</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">비고</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">사망일자</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">사망사유</th>
                                        <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700">사망장소</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contract.deads?.map((dead, index) => (
                                        <tr key={index} className="hover:bg-gray-50 text-center">
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900">
                                                {contract.mausoleum ? 
                                                    `${contract.mausoleum.complex || ''} ${contract.mausoleum.area || ''} ${contract.mausoleum.row_number || ''}-${contract.mausoleum.number || ''}`.trim() || '-'
                                                : '-'}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900">
                                                {formatContractAreaSizeWithUnit(contract.mausoleum?.area_size ?? contract.mausoleum?.size)}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900">
                                                {dead.burial_method_1 || dead.burial_method || '-'}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900 font-medium">
                                                {dead.name} {dead.gender ? `(${dead.gender})` : ''}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-xs text-gray-900">
                                                {dead.resident_number || dead.resident_id || '-'}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900">
                                                {dead.burial_method_2 || '-'}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900">
                                                {formatDate(dead.report_date || dead.burial_date)}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900">
                                                {dead.memo || '-'}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900">
                                                {formatDate(dead.death_date)}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900">
                                                {dead.death_cause || dead.cause_of_death || '-'}
                                            </td>
                                            <td className="border border-gray-300 p-2 text-sm text-gray-900">
                                                {dead.death_place || dead.place_of_death || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GraveRecord;